import React from 'react'
import { Box, TextField, Typography } from '@mui/material'
import IconPicker, { usePresentationIcons } from './IconPicker'

/** The keys an admin edits at both levels (library track and intervention override). */
export const PRESENTATION_KEYS = ['display_name', 'description', 'icon']

/** Form values ('' / null = inherit) from a stored presentation object. */
export function toFormValues(presentation) {
  const p = presentation && typeof presentation === 'object' ? presentation : {}
  return {
    display_name: p.display_name || '',
    description: p.description || '',
    icon: p.icon || null,
  }
}

/**
 * Merge edited form values back into a stored presentation object.
 * Blank fields are dropped (inherit). Other keys the server may hold at
 * this level (e.g. resource_categories) are preserved untouched.
 */
export function mergePresentation(stored, form) {
  const next = stored && typeof stored === 'object' ? { ...stored } : {}
  for (const key of PRESENTATION_KEYS) delete next[key]
  const displayName = (form.display_name || '').trim()
  const description = (form.description || '').trim()
  if (displayName) next.display_name = displayName
  if (description) next.description = description
  if (form.icon) next.icon = form.icon
  return next
}

/**
 * Map a data-service "Invalid presentation: presentation.x ..., ..."
 * message to per-field errors. Returns {} for unrelated messages.
 */
export function parsePresentationErrors(message) {
  if (typeof message !== 'string' || !message.includes('presentation.')) return {}
  const errors = {}
  for (const key of PRESENTATION_KEYS) {
    const match = message.match(new RegExp(`presentation\\.${key}\\b[^,]*`))
    if (match) {
      errors[key] = match[0].replace(`presentation.${key}`, key.replace('_', ' ')).trim()
    }
  }
  return errors
}

/**
 * Display name / description / icon inputs with inherited values as
 * placeholders, length counters mirroring the server limits, and inline
 * per-field errors.
 */
function PresentationFields({
  value,
  onChange,
  placeholders = {},
  errors = {},
  disabled = false,
  hints = {},
}) {
  const { limits } = usePresentationIcons()
  const set = (key, next) => onChange({ ...value, [key]: next })

  const counter = (text, max) => `${(text || '').length}/${max}`

  return (
    <Box display="grid" gridTemplateColumns="120px 1fr" gap={1.5} alignItems="center" maxWidth={700}>
      <Typography variant="body2" color="text.secondary">Display name</Typography>
      <TextField
        size="small"
        value={value.display_name}
        onChange={(e) => set('display_name', e.target.value)}
        placeholder={placeholders.display_name || ''}
        inputProps={{ maxLength: limits.display_name }}
        error={Boolean(errors.display_name)}
        helperText={
          errors.display_name
          || `${counter(value.display_name, limits.display_name)}${hints.display_name ? ` · ${hints.display_name}` : ''}`
        }
        disabled={disabled}
        fullWidth
      />

      <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'start', mt: 1 }}>
        Description
      </Typography>
      <TextField
        size="small"
        value={value.description}
        onChange={(e) => set('description', e.target.value)}
        placeholder={placeholders.description || ''}
        inputProps={{ maxLength: limits.description }}
        error={Boolean(errors.description)}
        helperText={
          errors.description
          || `${counter(value.description, limits.description)}${hints.description ? ` · ${hints.description}` : ''}`
        }
        multiline
        rows={2}
        disabled={disabled}
        fullWidth
      />

      <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'start', mt: 0.5 }}>
        Icon
      </Typography>
      <IconPicker
        value={value.icon}
        onChange={(icon) => set('icon', icon)}
        placeholder={placeholders.icon}
        disabled={disabled}
        error={errors.icon}
        helperText={hints.icon}
      />
    </Box>
  )
}

export default PresentationFields
